import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const API = `/api`;
const MAX_COMPARE = 3;

export default function Compare() {
  const [societes, setSocietes] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/knowledge`)
      .then((r) => r.json())
      .then((data) => {
        const s = data.filter((i) => i.type === "societes" && (i.fields || []).length > 0);
        setSocietes(s);
        const prio = ["srl", "public-company-limited-by-shares", "societe-cooperative"];
        setSelected(
          [...s]
            .sort((a, b) => {
              const ia = prio.indexOf(a.slug);
              const ib = prio.indexOf(b.slug);
              return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
            })
            .slice(0, 2)
            .map((x) => x.slug)
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const toggle = (slug) =>
    setSelected((sel) =>
      sel.includes(slug)
        ? sel.filter((s) => s !== slug)
        : sel.length < MAX_COMPARE
        ? [...sel, slug]
        : sel
    );

  const chosen = selected
    .map((slug) => societes.find((s) => s.slug === slug))
    .filter(Boolean);

  const rows = useMemo(() => {
    const seen = new Set();
    const out = [];
    const ordered = [...chosen].sort((a, b) =>
      a.slug === "srl" ? -1 : b.slug === "srl" ? 1 : 0
    );
    for (const s of ordered) {
      for (const f of s.fields || []) {
        const key = `${f.section}||${f.label}`;
        if (!seen.has(key)) {
          seen.add(key);
          out.push({ section: f.section, label: f.label, key });
        }
      }
    }
    return out;
  }, [chosen]);

  const cellOf = (soc, row) =>
    (soc.fields || []).find((f) => f.section === row.section && f.label === row.label)?.html;

  return (
    <div className="pt-40 pb-24 sm:pb-32 min-h-screen" data-testid="compare-page">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <Link
          to="/ressources"
          data-testid="compare-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" /> Ressources
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="label-accent" data-testid="compare-overline">Comparateur</span>
          <h1 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Comparer les{" "}
            <em className="italic text-gold-gradient not-italic">formes de sociétés.</em>
          </h1>
          <p className="mt-6 text-sm sm:text-base text-[#A0A5B1] max-w-2xl leading-relaxed">
            Personnalité, capital, titres, responsabilités, décision — les critères clés,
            côte à côte. Choisissez jusqu'à {MAX_COMPARE} formes.
          </p>
        </motion.div>

        {loading ? (
          <p className="mt-20 text-[#626773] font-mono text-sm" data-testid="compare-loading">
            Chargement…
          </p>
        ) : (
          <>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="compare-selector">
              {societes.map((s) => {
                const active = selected.includes(s.slug);
                return (
                  <button
                    key={s.slug}
                    onClick={() => toggle(s.slug)}
                    data-testid={`compare-toggle-${s.slug}`}
                    className={`text-xs uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors duration-300 ${
                      active
                        ? "bg-gold text-ink border-gold font-medium"
                        : "border-white/15 text-[#A0A5B1] hover:border-gold/60 hover:text-gold"
                    }`}
                  >
                    {s.title}
                  </button>
                );
              })}
            </div>

            {chosen.length < 2 ? (
              <p className="mt-16 font-serif text-2xl text-[#A0A5B1]" data-testid="compare-empty">
                Sélectionnez au moins deux formes de sociétés pour lancer la comparaison.
              </p>
            ) : (
              <div className="mt-12 overflow-x-auto border border-white/10" data-testid="compare-table-wrap">
                <table className="w-full min-w-[720px] border-collapse" data-testid="compare-table">
                  <thead>
                    <tr className="bg-ink-card">
                      <th className="sticky left-0 bg-ink-card text-left px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626773] w-56">
                        Critère
                      </th>
                      {chosen.map((s) => (
                        <th key={s.slug} className="text-left px-5 py-4 align-top">
                          <Link
                            to={`/ressources/societes/${s.slug}`}
                            data-testid={`compare-head-${s.slug}`}
                            className="group font-serif text-lg text-[#FAF8F5] hover:text-gold-light transition-colors"
                          >
                            {s.title}
                            <ArrowUpRight className="inline-block ml-2 w-4 h-4 text-[#626773] group-hover:text-gold transition-colors" />
                          </Link>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {rows.map((row, ri) => {
                      const newSection = ri === 0 || rows[ri - 1].section !== row.section;
                      return [
                        newSection && (
                          <tr key={`${row.key}-section`} className="bg-white/[0.03]">
                            <td
                              colSpan={chosen.length + 1}
                              className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold"
                            >
                              {row.section}
                            </td>
                          </tr>
                        ),
                        <tr key={row.key} data-testid={`compare-row-${ri}`}>
                          <td className="sticky left-0 bg-ink px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80 align-top">
                            {row.label}
                          </td>
                          {chosen.map((s) => {
                            const html = cellOf(s, row);
                            return (
                              <td key={s.slug} className="px-5 py-4 align-top text-sm text-[#A0A5B1] leading-relaxed">
                                {html ? (
                                  <div className="fiche-content" dangerouslySetInnerHTML={{ __html: html }} />
                                ) : (
                                  <span className="text-[#3a3d46]">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>,
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-16 bg-ink-card border border-gold/20 p-8 sm:p-12 flex flex-wrap items-center justify-between gap-6" data-testid="compare-cta">
              <div className="max-w-xl">
                <span className="label-accent">Le bon véhicule pour votre projet</span>
                <p className="mt-4 font-serif text-2xl sm:text-3xl leading-snug">
                  Le choix d'une forme se décide{" "}
                  <em className="italic text-gold-gradient not-italic">avec un conseil.</em>
                </p>
                <p className="mt-3 text-sm text-[#A0A5B1] leading-relaxed">
                  Fiscalité, gouvernance, projet de transmission — un avocat vous aide à
                  arbitrer. Premier échange sans engagement.
                </p>
              </div>
              <a
                href="/#contact"
                data-testid="compare-cta-contact"
                className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-4 hover:bg-gold-light transition-colors shrink-0"
              >
                Échanger avec un avocat
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
